import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/StatusBadge';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Filter, Loader2, Eye, Search, ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PINNED_ACTIONS = 'sticky right-0 border-l shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.18)] min-w-[88px] text-center';

const toOptions = (values) => (values || []).map(v => ({ value: v, label: v }));

export default function UnitConversionsPage() {
  const [conversions, setConversions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Multi-select column filters, and the distinct values that fill them
  const [fromFilter, setFromFilter] = useState([]);
  const [toFilter, setToFilter] = useState([]);
  const [filterOptionsData, setFilterOptionsData] = useState({});

  const { canEdit } = useAuth();
  const navigate = useNavigate();

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    axios.get(`${API_URL}/unit-conversions/filter-options`)
      .then(res => setFilterOptionsData(res.data))
      .catch(() => { /* dropdowns just stay empty if this fails */ });
  }, []);

  const filterOptions = useMemo(() => ({
    from: toOptions(filterOptionsData.conversion_from),
    to: toOptions(filterOptionsData.conversion_to),
  }), [filterOptionsData]);

  const hasActiveFilters = [fromFilter, toFilter].some(f => f.length > 0);
  const clearAllFilters = () => {
    setFromFilter([]);
    setToFilter([]);
  };

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (debouncedSearch) params.append('search', debouncedSearch);
    fromFilter.forEach(v => params.append('conversion_from', v));
    toFilter.forEach(v => params.append('conversion_to', v));
    params.append('page', String(currentPage));
    params.append('page_size', String(pageSize));
    axios.get(`${API_URL}/unit-conversions?${params}`)
      .then(res => {
        setConversions(res.data.items);
        setTotal(res.data.total);
        setTotalPages(res.data.total_pages);
      })
      .catch(() => toast.error('Failed to load unit conversions'))
      .finally(() => setLoading(false));
  }, [statusFilter, debouncedSearch, fromFilter, toFilter, currentPage, pageSize]);

  // Back to page 1 whenever the query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, fromFilter, toFilter, pageSize]);

  // Downloads every approved conversion, ordered by ID
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/unit-conversions/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'unit_conversions.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export unit conversions');
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="unit-conversions-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Unit Conversions</h1>
            <p className="text-muted-foreground mt-1">All unit conversions: existing ones and those submitted through Atlas</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={handleExport} disabled={exporting} data-testid="export-conversions-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export
            </Button>
            {canEdit() && (
              <Link to="/unit-conversions/new">
                <Button className="btn-primary gap-2" data-testid="create-conversion-btn">
                  <Plus className="w-4 h-4" />
                  New Conversion
                </Button>
              </Link>
            )}
          </div>
        </div>

        <Card className="border-gray-200">
          <CardContent className="p-4 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ID, units, created by..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 input-eco"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 input-eco" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1">Filters:</span>
              <MultiSelectFilter label="From" options={filterOptions.from} selected={fromFilter} onSelectionChange={setFromFilter} testIdPrefix="uc-from-" />
              <MultiSelectFilter label="To" options={filterOptions.to} selected={toFilter} onSelectionChange={setToFilter} testIdPrefix="uc-to-" />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={clearAllFilters} data-testid="clear-filters-btn">
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : total === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No unit conversions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-[850px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="table-dense min-w-[80px]">ID</TableHead>
                      <TableHead className="table-dense min-w-[140px]">From</TableHead>
                      <TableHead className="table-dense min-w-[140px]">To</TableHead>
                      <TableHead className="table-dense min-w-[110px]">Value</TableHead>
                      <TableHead className="table-dense min-w-[110px]">Status</TableHead>
                      <TableHead className="table-dense min-w-[200px]">Created By</TableHead>
                      <TableHead className="table-dense min-w-[110px]">Created At</TableHead>
                      <TableHead className={`table-dense ${PINNED_ACTIONS} bg-gray-50`}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversions.map((c) => (
                      <TableRow key={`${c.source}-${c.id}`} className="table-dense" data-testid={`conversion-row-${c.source}-${c.id}`}>
                        <TableCell className="font-mono text-sm whitespace-nowrap">{c.source === 'existing' ? c.id : (c.unit_conversion_id || '—')}</TableCell>
                        <TableCell className="whitespace-nowrap font-medium">{c.conversion_from}</TableCell>
                        <TableCell className="whitespace-nowrap font-medium">{c.conversion_to}</TableCell>
                        <TableCell className="font-mono text-sm whitespace-nowrap">{c.value}</TableCell>
                        <TableCell><StatusBadge status={c.status} /></TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{c.user_email || '—'}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</TableCell>
                        <TableCell className={`${PINNED_ACTIONS} bg-white`}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/unit-conversions/${c.id}${c.source === 'existing' ? '?source=existing' : ''}`)}
                            title="View Details"
                            data-testid={`view-conversion-${c.source}-${c.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {!loading && totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} results
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
                      <SelectTrigger className="w-[70px] h-8" data-testid="page-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="prev-page-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="next-page-btn">
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
